<template>
  <div class="orientation-layout">
    <div class="header-logo">
      <div class="logo-box">MUIC</div>
      <div class="logo-text">
        <span class="logo-title">MUIC</span>
        <span class="logo-subtitle">ORIENTATION CHECK-IN</span>
      </div>
    </div>
    
    <transition name="fade" mode="out-in">
      <RoomSelection 
        v-if="currentStep === 'selection'" 
        :initialAccountType="selectedAccountType"
        :rooms="rooms"
        @continue="onAccountSelected" 
        key="selection" 
      />
      
      <div v-else-if="currentStep === 'checkin'" key="checkin" style="width: 100%; display: flex; flex-direction: column; align-items: center;">

        <CheckInCard :accountType="selectedAccountType" :roomId="selectedRoomId" :providerConfig="selectedProviderConfig" @back="goBack" @success="onLoginSuccess" />

        <!-- Bottom Footer -->
        <div class="footer-note">
          <a-icon type="safety-certificate" />
          Sign-in happens securely on Microsoft. We never see your password or verification code.
        </div>
      </div>

      <CheckInSuccess 
        v-else-if="currentStep === 'success'" 
        key="success"
        :accountData="successData"
        :accountType="selectedAccountType"
        :roomId="selectedRoomId"
        @logout="onLogout"
      />
    </transition>
  </div>
</template>

<script>
import RoomSelection from './components/RoomSelection.vue';
import CheckInCard from './components/CheckInCard.vue';
import CheckInSuccess from './components/CheckInSuccess.vue';
import axios from 'axios';

export default {
  name: 'OrientationStudentCheckIn',
  components: {
    RoomSelection,
    CheckInCard,
    CheckInSuccess
  },
  data() {
    return {
      currentStep: 'selection', // 'selection' | 'checkin' | 'success'
      selectedAccountType: 'MUIC Account',
      selectedRoomId: null,
      successData: null,
      rooms: [],
      configData: null,
    };
  },
  computed: {
    selectedProviderConfig() {
      if (!this.configData) return null;
      if (this.selectedAccountType === 'MUIC Account') return this.configData.providers.muic;
      if (this.selectedAccountType === 'MU Account') return this.configData.providers.mu;
      return null;
    }
  },
  mounted() {
    this.fetchConfig();
  },
  beforeDestroy() {
    // Clean up if needed
  },
  created() {
    // Check if type is provided in query string
    if (this.$route.query.type) {
      if (this.$route.query.type === 'success') {
        // If user manually reloads on success page without data, clear the query
        this.$router.replace({ query: {} }).catch(() => {});
      } else if (this.$route.query.type === 'MUAccount') {
        this.selectedAccountType = 'MU Account';
      } else if (this.$route.query.type === 'MUICAccount') {
        this.selectedAccountType = 'MUIC Account';
      } else {
        this.selectedAccountType = this.$route.query.type;
      }
    }
    this.fetchRooms();
  },
  methods: {
    async fetchRooms() {
      try {
        const response = await axios.get('/orientation-game/api/v1/public/rooms/');
        console.log("Rooms Data:", response.data);
        if (response.status === 200) {
          this.rooms = response.data;
        }
      } catch (error) {
        console.error("Error fetching rooms:", error);
      }
    },
    async fetchConfig() {
      // Mockup config as requested
      this.configData = {
        "providers": {
          "muic": {
            "tenant_id": "5a8c21e3-7c52-40e3-8d81-deba37efd8dc",
            "public_client_id": "f94faf41-4c3c-4f82-9bf7-cc3eb9613766",
            "authority": "https://login.microsoftonline.com/5a8c21e3-7c52-40e3-8d81-deba37efd8dc",
            "scope": "api://f94faf41-4c3c-4f82-9bf7-cc3eb9613766/orientation.checkin",
            "allowed_domains": ["muic.io"],
            "ready": true
          },
          "mu": {
            "tenant_id": "9bc585f9-a8b8-431c-9013-efa7b2b40cdf",
            "public_client_id": "151519df-01b3-4d7b-b605-1d19da185c4c",
            "authority": "https://login.microsoftonline.com/9bc585f9-a8b8-431c-9013-efa7b2b40cdf",
            "scope": "api://151519df-01b3-4d7b-b605-1d19da185c4c/orientation.checkin",
            "allowed_domains": ["student.mahidol.ac.th", "mahidol.ac.th"],
            "ready": true
          }
        }
      };

      try {
        const response = await axios.get('/orientation-game/api/v1/public/config/');
        if (response.status === 200) {
          console.log("Config Data:", response.data);
          // If you want to use the actual API response later, you can do:
          // this.configData = response.data;
        }
      } catch (error) {
        console.error("Error fetching config:", error);
      }
    },
    onAccountSelected(selection) {
      if (typeof selection === 'string') {
        this.selectedAccountType = selection;
      } else {
        this.selectedAccountType = selection.type;
        this.selectedRoomId = selection.roomId;
      }
      this.currentStep = 'checkin';
      const urlType = this.selectedAccountType.replace(/\s+/g, '');
      this.$router.replace({ query: { ...this.$route.query, type: urlType } }).catch(() => {});
    },
    goBack() {
      this.currentStep = 'selection';
      const query = { ...this.$route.query };
      delete query.type;
      this.$router.replace({ query }).catch(() => {});
    },
    onLoginSuccess({ account, accessToken }) {
      this.successData = { ...account, accessToken };
      this.currentStep = 'success';
      this.$router.replace({ query: { type: 'success' } }).catch(() => {});
    },
    onLogout() {
      // Return to selection step ("Choose your Sign in type")
      this.successData = null;
      this.currentStep = 'selection';
      this.$router.replace({ query: {} }).catch(() => {});
    }
  }
};
</script>

<style scoped>
/* Layout and Background */
.orientation-layout {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  background: linear-gradient(135deg, #fdfbfb 0%, #ebedee 100%);
  position: relative;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
  padding: 100px 20px 40px; /* Top padding ensures space for the absolute header */
}

/* Header Logo for Checkin step */
.header-logo {
  position: absolute;
  top: 40px;
  left: 40px;
  display: flex;
  align-items: center;
  gap: 12px;
}
.logo-box {
  background: #3e247a;
  color: white;
  font-weight: bold;
  font-size: 13px;
  width: 44px;
  height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  letter-spacing: 0.5px;
}
.logo-text {
  display: flex;
  flex-direction: column;
}
.logo-title {
  color: #3e247a;
  font-weight: 800;
  font-size: 16px;
  line-height: 1.1;
}
.logo-subtitle {
  color: #6c757d;
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 1px;
}

/* Footer for Checkin step */
.footer-note {
  margin-top: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  color: #888;
  font-size: 11px;
  text-align: center;
  z-index: 2;
}

/* Fade Transition */
.fade-enter-active, .fade-leave-active {
  transition: opacity 0.3s ease;
}
.fade-enter, .fade-leave-to {
  opacity: 0;
}

/* Responsive adjustments */
@media (max-width: 600px) {
  .orientation-layout {
    padding-top: 80px;
  }
  .header-logo {
    top: 20px;
    left: 20px;
  }
  .footer-note {
    font-size: 10px;
  }
}
</style>
